import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/**
 * Aranabilir çoklu-seçim dropdown.
 *
 * SearchableSelect'in çoklu-seçim sürümü: tek kutuya tıklanır, içine yazarak
 * seçenek aranır ve birden fazlası işaretlenebilir (örn. "Kauçuk" + "Kum"
 * aynı anda). Seçim yapınca popover kapanmaz.
 *
 * Props:
 *  - value: seçili id'lerin dizisi (örn. ["kaucuk", "kum"])
 *  - onChange(ids): seçim değişince güncel id dizisiyle çağrılır
 *  - options: [{ id, name }]
 *  - allLabel: hiçbir şey seçili değilken gösterilen etiket
 *  - searchPlaceholder: arama kutusu placeholder'ı
 *  - emptyText: eşleşme yokken gösterilen metin
 *  - className: tetikleyici buton ek sınıfları
 */
export function MultiSelectSearch({
  value = [],
  onChange,
  options = [],
  allLabel = "Tümü",
  searchPlaceholder = "Ara...",
  emptyText = "Sonuç bulunamadı.",
  className,
}) {
  const [open, setOpen] = React.useState(false);
  const selectedSet = new Set(value);
  const selectedNames = options
    .filter((o) => selectedSet.has(o.id))
    .map((o) => o.name);
  const label =
    selectedNames.length === 0
      ? allLabel
      : selectedNames.length <= 2
      ? selectedNames.join(", ")
      : `${selectedNames.length} seçili`;

  const toggle = (id) => {
    if (selectedSet.has(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
        >
          <span
            className={cn(
              "truncate",
              selectedNames.length ? "" : "text-muted-foreground"
            )}
          >
            {label}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {value.length > 0 && (
                <CommandItem value="__temizle__" onSelect={() => onChange([])}>
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  <span className="text-blue-600">Seçimi temizle</span>
                </CommandItem>
              )}
              {options.map((o) => (
                <CommandItem
                  key={o.id}
                  value={o.name}
                  onSelect={() => toggle(o.id)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedSet.has(o.id) ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {o.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default MultiSelectSearch;
